import { emptyArray, emptyObject } from 'ui-components/default-props/empty';

import { baseReducer } from '../core/base-reducer';
import type { ScanMode } from '../../pages/pick-pack-station/main/modes';
import { SCAN_MODES } from '../../pages/pick-pack-station/main/modes';
import { CSW_CREATE_LABEL, CSW_CREATE_LABEL_SUCCESS, CSW_CREATE_LABEL_FAILURE } from '../csw/csw.actions';
import * as Actions from './pick-pack-station-scan.actions';
import { namespace } from './pick-pack-station-scan.reducer.metadata';

export type PickPackStationScanQueue = {
  fulfillmentPlanIds: string[];
  currentIndex: number;
  batchName: string;
  isActive: boolean;
};

export type PickPackStationScanState = {
  activeMode: ScanMode;
  fulfillmentPlanId: string | null;
  isLoading: boolean;
  hasError: boolean;
  isPurchasing: boolean;
  isOrderSearch: boolean;
  searchValue: string;
  orderSearchResults: unknown[];
  verifiedItemCounts: Record<string, number>;
  isVerified: boolean;
  isSubmittingVerification: boolean;
  isAutoConfirmArmed: boolean;
  hasVerificationError: boolean;
  hasEditedVerification: boolean;
  lastScanResult: Actions.LastScanResult | null;
  queue: PickPackStationScanQueue;
};

export const emptyQueue: PickPackStationScanQueue = Object.freeze({
  fulfillmentPlanIds: emptyArray as string[],
  currentIndex: 0,
  batchName: '',
  isActive: false,
});

const initialState: PickPackStationScanState = {
  activeMode: SCAN_MODES.verifyAndPrint,
  fulfillmentPlanId: null,
  isLoading: false,
  hasError: false,
  isPurchasing: false,
  isOrderSearch: false,
  searchValue: '',
  orderSearchResults: emptyArray as unknown[],
  verifiedItemCounts: emptyObject as Record<string, number>,
  isVerified: false,
  isSubmittingVerification: false,
  isAutoConfirmArmed: true,
  hasVerificationError: false,
  hasEditedVerification: false,
  lastScanResult: null,
  queue: emptyQueue,
};

const actionHandlerMap = {
  [Actions.SET_PICK_PACK_STATION_SCAN_MODE]: (state: PickPackStationScanState, { activeMode }: { activeMode: ScanMode }) => ({
    ...state,
    activeMode,
  }),
  [Actions.SET_PICK_PACK_STATION_FULFILLMENT_PLAN_ID]: (
    state: PickPackStationScanState,
    { fulfillmentPlanId }: { fulfillmentPlanId: string | null },
  ) => ({
    ...state,
    fulfillmentPlanId,
    isLoading: false,
    isPurchasing: false,
    isOrderSearch: false,
    orderSearchResults: emptyArray,
    searchValue: '',
    verifiedItemCounts: emptyObject as Record<string, number>,
    isVerified: false,
    isSubmittingVerification: false,
    isAutoConfirmArmed: true,
    hasVerificationError: false,
    hasEditedVerification: false,
    lastScanResult: null,
  }),
  [Actions.SEARCH_PICK_PACK_STATION_ORDER]: (state: PickPackStationScanState, { searchValue }: { searchValue: string }) => ({
    ...state,
    isLoading: true,
    hasError: false,
    isOrderSearch: false,
    orderSearchResults: emptyArray,
    searchValue,
  }),
  [Actions.PICK_PACK_STATION_SELECT_FULFILLMENT_PLAN]: (state: PickPackStationScanState) => ({
    ...state,
    isLoading: true,
    hasError: false,
  }),
  [Actions.PICK_PACK_STATION_ORDER_SEARCH_RESULTS]: (state: PickPackStationScanState, { orders }: { orders: unknown[] }) => ({
    ...state,
    isLoading: false,
    isOrderSearch: true,
    orderSearchResults: orders,
  }),
  [Actions.PICK_PACK_STATION_ORDER_SEARCH_ERROR]: (state: PickPackStationScanState) => ({
    ...state,
    fulfillmentPlanId: null,
    isLoading: false,
    hasError: true,
    isOrderSearch: false,
    orderSearchResults: emptyArray,
  }),
  [Actions.SET_PICK_PACK_STATION_SEARCH_INPUT]: (state: PickPackStationScanState, { searchValue }: { searchValue: string }) => ({
    ...state,
    searchValue,
  }),
  [Actions.CLEAR_PICK_PACK_STATION_ORDER_SEARCH]: (state: PickPackStationScanState) => ({
    ...state,
    isLoading: false,
    hasError: false,
    isOrderSearch: false,
    orderSearchResults: emptyArray,
    searchValue: '',
  }),
  [Actions.RESET_PICK_PACK_STATION_ORDER]: (state: PickPackStationScanState) => ({
    ...state,
    fulfillmentPlanId: null,
    isLoading: false,
    hasError: false,
    isPurchasing: false,
    isOrderSearch: false,
    orderSearchResults: emptyArray,
    searchValue: '',
    verifiedItemCounts: emptyObject as Record<string, number>,
    isVerified: false,
    isSubmittingVerification: false,
    isAutoConfirmArmed: true,
    hasVerificationError: false,
    hasEditedVerification: false,
    lastScanResult: null,
  }),
  [Actions.VERIFY_PICK_PACK_STATION_ITEM]: (
    state: PickPackStationScanState,
    { itemId, verifiedCount }: { itemId: string; verifiedCount: number },
  ) => ({
    ...state,
    verifiedItemCounts: { ...state.verifiedItemCounts, [itemId]: verifiedCount },
    isAutoConfirmArmed: true,
    hasEditedVerification: false,
    hasVerificationError: false,
  }),
  [Actions.CLEAR_PICK_PACK_STATION_ITEM]: (state: PickPackStationScanState, { itemId }: { itemId: string }) => ({
    ...state,
    verifiedItemCounts: { ...state.verifiedItemCounts, [itemId]: 0 },
    isAutoConfirmArmed: true,
    hasVerificationError: false,
  }),
  [Actions.VERIFY_ALL_PICK_PACK_STATION_ITEMS]: (
    state: PickPackStationScanState,
    { items }: { items: ReadonlyArray<{ itemId: string; quantity: number }> },
  ) => ({
    ...state,
    verifiedItemCounts: Object.fromEntries(items.map(({ itemId, quantity }) => [itemId, quantity])),
    isAutoConfirmArmed: true,
    hasEditedVerification: false,
    hasVerificationError: false,
  }),
  [Actions.CLEAR_ALL_PICK_PACK_STATION_ITEMS]: (state: PickPackStationScanState) => ({
    ...state,
    verifiedItemCounts: emptyObject as Record<string, number>,
    isAutoConfirmArmed: true,
    hasVerificationError: false,
  }),
  [Actions.SUBMIT_PICK_PACK_STATION_VERIFICATION]: (state: PickPackStationScanState) => ({
    ...state,
    isSubmittingVerification: true,
    isAutoConfirmArmed: false,
    hasVerificationError: false,
  }),
  [Actions.SUBMIT_PICK_PACK_STATION_VERIFICATION_SUCCESS]: (
    state: PickPackStationScanState,
    { fulfillmentPlanId }: { fulfillmentPlanId: string },
  ) => {
    if (fulfillmentPlanId !== state.fulfillmentPlanId) return state;
    return {
      ...state,
      isVerified: true,
      isSubmittingVerification: false,
      hasEditedVerification: false,
    };
  },
  [Actions.SUBMIT_PICK_PACK_STATION_VERIFICATION_ERROR]: (
    state: PickPackStationScanState,
    { fulfillmentPlanId }: { fulfillmentPlanId: string },
  ) => {
    if (fulfillmentPlanId !== state.fulfillmentPlanId) return state;
    return {
      ...state,
      isSubmittingVerification: false,
      hasVerificationError: true,
    };
  },
  [Actions.INITIALIZE_PICK_PACK_STATION_SCAN_QUEUE]: (
    state: PickPackStationScanState,
    { fulfillmentPlanIds, batchName }: { fulfillmentPlanIds: string[]; batchName: string },
  ) => ({
    ...state,
    queue: { fulfillmentPlanIds, currentIndex: 0, batchName, isActive: true },
    fulfillmentPlanId: fulfillmentPlanIds[0] ?? null,
  }),
  [Actions.ADVANCE_PICK_PACK_STATION_QUEUE]: (state: PickPackStationScanState) => {
    const { queue } = state;
    if (!queue.isActive) return state;
    const nextIndex = queue.currentIndex + 1;
    return {
      ...state,
      queue: { ...queue, currentIndex: nextIndex },
      fulfillmentPlanId: queue.fulfillmentPlanIds[nextIndex] ?? null,
    };
  },
  [Actions.CLEAR_PICK_PACK_STATION_SCAN_QUEUE]: (state: PickPackStationScanState) => ({
    ...state,
    queue: emptyQueue,
  }),
  [Actions.EDIT_PICK_PACK_STATION_VERIFICATION]: (
    state: PickPackStationScanState,
    { items }: { items: ReadonlyArray<{ itemId: string; quantity: number }> },
  ) => ({
    ...state,
    isVerified: false,
    isAutoConfirmArmed: false,
    hasVerificationError: false,
    hasEditedVerification: true,
    verifiedItemCounts: Object.fromEntries(items.map(({ itemId, quantity }) => [itemId, quantity])),
  }),
  [Actions.SET_PICK_PACK_STATION_LAST_SCAN_RESULT]: (
    state: PickPackStationScanState,
    { kind, scannedValue, itemId }: Pick<Actions.LastScanResult, 'kind' | 'scannedValue' | 'itemId'>,
  ) => ({
    ...state,
    lastScanResult: {
      sequence: (state.lastScanResult?.sequence ?? 0) + 1,
      kind,
      scannedValue,
      itemId,
    },
  }),
  [CSW_CREATE_LABEL]: (state: PickPackStationScanState, { fulfillmentPlanId }: { fulfillmentPlanId: string }) => {
    if (fulfillmentPlanId !== state.fulfillmentPlanId) return state;
    return { ...state, isPurchasing: true };
  },
  [CSW_CREATE_LABEL_SUCCESS]: (state: PickPackStationScanState, { fulfillmentPlanId }: { fulfillmentPlanId: string }) => {
    if (fulfillmentPlanId !== state.fulfillmentPlanId) return state;
    return { ...state, isPurchasing: false };
  },
  [CSW_CREATE_LABEL_FAILURE]: (
    state: PickPackStationScanState,
    { fulfillmentPlanId, isLabelPurchaseComplete }: { fulfillmentPlanId?: string; isLabelPurchaseComplete?: boolean },
  ) => {
    if (fulfillmentPlanId !== state.fulfillmentPlanId || !isLabelPurchaseComplete) return state;
    return { ...state, isPurchasing: false };
  },
};

export const ConnectedActionHandlerMap = baseReducer(initialState, actionHandlerMap);
export { namespace };
